import React, { useState, useContext, useRef, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { useSearchParams } from 'react-router-dom';

import SidePanel from '../components/Common/SidePanel';
import MobileMenuToggle from '../components/Common/MobileMenuToggle';
import { Barcode, Edit2, AlertCircle, CheckCircle, Loader, Camera, X } from 'lucide-react';
import { barcodeService, recordService } from '../services/api';
import Quagga from '@ericblade/quagga2';

const ScanUpdate = () => {
  const { user } = useContext(AuthContext);
  const { isCollapsed } = useSidebar();
  const [searchParams] = useSearchParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [useCamera, setUseCamera] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [success, setSuccess] = useState(false);
  const inputRef = useRef(null);
  const quaggaRef = useRef(null);
  const mobileQuaggaRef = useRef(null);
  const [formData, setFormData] = useState({
    status: 'PENDING',
    remarks: ''
  });

  useEffect(() => {
    // Check if recordId is in query params and load that record
    const recordId = searchParams.get('recordId');
    if (recordId) {
      loadRecordById(recordId);
    }
  }, [searchParams]);

  const loadRecordById = async (recordId) => {
    try {
      setScanning(true);
      setError('');
      const response = await recordService.getById(recordId);
      const recordData = response.data?.data?.record || response.data?.data;
      
      setScannedData({
        barcodeNumber: recordData.barcodeNumber,
        scanned: true,
        recordId: recordData.id,
        lastUpdatedAt: recordData.lastUpdatedAt
      });
      
      setFormData({ 
        status: recordData.status || 'PENDING', 
        remarks: ''
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load record');
    } finally {
      setScanning(false);
    }
  };

  useEffect(() => {
    if (useCamera && !cameraActive) {
      initCamera();
    }
    return () => {
      if (cameraActive) {
        Quagga.stop();
      }
    };
  }, [useCamera, cameraActive]);

  const initCamera = async () => {
    try {
      // Determine which ref to use based on screen size
      const isMobile = window.innerWidth < 1024; // lg breakpoint
      const targetRef = isMobile ? mobileQuaggaRef : quaggaRef;

      Quagga.init(
        {
          inputStream: {
            type: 'LiveStream',
            constraints: {
              width: { min: 640 },
              height: { min: 480 },
              facingMode: 'environment',
              aspectRatio: { min: 4 / 3, max: 16 / 9 }
            },
            target: targetRef.current
          },
          decoder: {
            workers: 2,
            debug: false,
            multiple: false
          },
          locate: true,
          frequency: 10,
          maxImageSize: 5000000
        },
        (err) => {
          if (err) {
            setError('Camera access denied or not available. Please use manual input.');
            setUseCamera(false);
            return;
          }
          Quagga.start();
          setCameraActive(true);
          setupQuaggaListeners();
        }
      );
    } catch (err) {
      setError('Failed to initialize camera. Please use manual input.');
      setUseCamera(false);
    }
  };

  const setupQuaggaListeners = () => {
    Quagga.onDetected(handleBarcodeDetected);
  };

  const handleBarcodeDetected = (result) => {
    if (result.codeResult && result.codeResult.code) {
      const barcode = result.codeResult.code;
      
      // Close camera IMMEDIATELY - synchronously, before any async calls
      Quagga.stop();
      Quagga.offDetected();
      setCameraActive(false);
      setUseCamera(false);
      
      // Validate barcode (async - but camera is already closed)
      performBarcodeValidation(barcode);
    }
  };

  const performBarcodeValidation = async (code) => {
    setScanning(true);
    setError('');
    setBarcodeInput('');

    try {
      const response = await barcodeService.validateForUpdate(code);
      const recordData = response.data?.data?.record;
      
      setScannedData({
        barcodeNumber: response.data?.data?.barcodeNumber || code,
        scanned: true,
        recordId: recordData?.id,
        lastUpdatedAt: recordData?.lastUpdatedAt
      });
      
      // Pre-fill form with current record status if it exists
      if (recordData?.currentStatus) {
        setFormData({ 
          status: recordData.currentStatus, 
          remarks: ''
        });
      } else {
        setFormData({ status: 'PENDING', remarks: '' });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid barcode');
    } finally {
      setScanning(false);
      inputRef.current?.focus();
    }
  };

  const stopCamera = () => {
    Quagga.stop();
    Quagga.offDetected();
    setCameraActive(false);
    setUseCamera(false);
    setBarcodeInput('');
  };

  const validateBarcode = async (code) => {
    if (!code.trim()) {
      setError('Please enter a barcode');
      return;
    }

    await performBarcodeValidation(code);
  };

  const handleScan = async (e) => {
    e.preventDefault();
    if (!barcodeInput.trim()) {
      setError('Please enter a barcode');
      return;
    }

    validateBarcode(barcodeInput);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!scannedData) return;

    // Validate remarks for RETURN status
    if (formData.status === 'RETURN' && !formData.remarks.trim()) {
      setError('Remarks are mandatory when marking as RETURN. Please provide a reason for the return.');
      return;
    }

    setUpdating(true);
    setError('');
    setSuccess(false);

    try {
      await recordService.updateStatus(scannedData.barcodeNumber, {
        status: formData.status,
        remarks: formData.remarks || null
      });

      setSuccess(true);
      setScannedData(null);
      setFormData({ status: 'PENDING', remarks: '' });
      
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update record');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="flex h-screen bg-slate-50">
      <MobileMenuToggle onClick={() => setIsSidebarOpen(true)} isSidebarOpen={isSidebarOpen} />
      <SidePanel isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex-1 flex flex-col overflow-hidden transition-all duration-300">
        <main className="flex-1 overflow-y-auto pt-28 md:pt-0">
          <div className="p-6 md:p-8  mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Scan & Update Record</h1>
              <p className="text-slate-600">Scan barcode and update service record status</p>
            </div>

            {success && (
              <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-300 rounded-xl flex gap-3 shadow-sm">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-green-800 font-medium">Record updated successfully!</p>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-rose-50 border border-red-300 rounded-xl flex gap-3 shadow-sm">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Barcode Scanner - Full width on mobile, left column on desktop */}
              <div className="lg:col-span-1 bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg p-6 md:p-8 border border-slate-200">
                <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                    <Barcode className="w-6 h-6 text-white" />
                  </div>
                  Scan Barcode
                </h3>

                {useCamera ? (
                  <div className="space-y-4">
                    <div className="relative w-full rounded-2xl overflow-hidden bg-black shadow-xl border-4 border-blue-600" style={{ aspectRatio: '4/3' }}>
                      <div ref={quaggaRef} style={{ width: '100%', height: '100%' }} />
                      {cameraActive && (
                        <div className="absolute top-4 right-4 flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                          <div className="w-2.5 h-2.5 bg-white rounded-full animate-pulse"></div>
                          Ready
                        </div>
                      )}
                    </div>

                    <div className="p-4 bg-gradient-to-r from-blue-100 to-indigo-100 border-2 border-blue-400 rounded-xl">
                      <p className="text-sm font-semibold text-blue-900">
                        Point camera at barcode
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => stopCamera()}
                      className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-slate-600 to-slate-700 text-white py-3 rounded-xl hover:from-slate-700 hover:to-slate-800 transition shadow-md font-semibold text-base"
                    >
                      <X className="w-5 h-5" />
                      Close Camera
                    </button>
                  </div>
                ) : scannedData ? (
                  <div className="space-y-4">
                    <div className="p-5 bg-gradient-to-br from-green-50 to-emerald-50 border-3 border-green-400 rounded-2xl shadow-sm">
                      <p className="text-sm font-semibold text-green-900 mb-2">Successfully Scanned</p>
                      <p className="text-2xl font-mono font-bold text-green-700 mb-3">{scannedData.barcodeNumber}</p>
                      {scannedData.lastUpdatedAt && (
                        <p className="text-xs text-green-600 font-medium">
                          Last updated: {new Date(scannedData.lastUpdatedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setScannedData(null);
                        setFormData({ status: 'PENDING', remarks: '' });
                        setBarcodeInput('');
                        inputRef.current?.focus();
                      }}
                      className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-500 to-blue-500 text-white py-3 rounded-xl hover:from-indigo-600 hover:to-blue-600 transition shadow-md font-semibold text-base"
                    >
                      <Barcode className="w-5 h-5" />
                      Scan Next Barcode
                    </button>
                  </div>
                ) : (
                  <>
                    <form onSubmit={handleScan} className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Manual Barcode Entry</label>
                        <input
                          ref={inputRef}
                          type="text"
                          placeholder="Enter barcode..."
                          value={barcodeInput}
                          onChange={(e) => setBarcodeInput(e.target.value)}
                          autoFocus
                          className="w-full px-5 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-base font-semibold transition bg-white shadow-sm"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={scanning || !!scannedData}
                        className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition disabled:from-slate-400 disabled:to-slate-500 font-bold text-base shadow-md"
                      >
                        {scanning ? (
                          <>
                            <Loader className="w-5 h-5 animate-spin" />
                            Validating...
                          </>
                        ) : (
                          <>Scan</>
                        )}
                      </button>
                    </form>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-slate-300"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-gradient-to-br from-white to-slate-50 text-slate-500 font-medium">OR</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setUseCamera(true)}
                      disabled={scanning || !!scannedData}
                      className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 rounded-xl hover:from-green-600 hover:to-emerald-700 transition disabled:from-slate-400 disabled:to-slate-500 font-bold text-lg shadow-lg transform hover:scale-105"
                    >
                      <Camera className="w-6 h-6" />
                      Open Camera
                    </button>
                  </>
                )}
              </div>

              {/* Record Update Form - Right 2 columns on desktop, full width on mobile */}
              {scannedData && (
                <div className="lg:col-span-2 bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg p-6 md:p-8 border border-slate-200">
                  <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg">
                      <Edit2 className="w-6 h-6 text-white" />
                    </div>
                    Update Status
                  </h3>
                  <form onSubmit={handleUpdate} className="space-y-6">
                    {/* Current Status Badge */}
                    <div className="p-6 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border-3 border-blue-300 rounded-2xl shadow-sm">
                      <p className="text-xs text-blue-600 font-bold uppercase tracking-wider mb-2">Current Status</p>
                      <p className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{formData.status}</p>
                    </div>

                    {/* Status Buttons - Larger touch targets for mobile */}
                    <div>
                      <label className="block text-sm font-bold text-slate-800 mb-4 uppercase tracking-wide">Select New Status</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {['PENDING', 'ASSISTED', 'HOLD', 'RETURN'].map((status) => (
                          <button
                            key={status}
                            type="button"
                            onClick={() => setFormData({ ...formData, status })}
                            className={`py-4 px-3 rounded-xl font-bold transition text-sm md:text-base shadow-md transform hover:scale-105 ${
                              formData.status === status
                                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white ring-3 ring-blue-300 shadow-lg'
                                : 'bg-gradient-to-r from-slate-100 to-slate-200 text-slate-800 hover:from-slate-200 hover:to-slate-300'
                            }`}
                          >
                            {status}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Remarks - Only for SERVICE_ENGINEER */}
                    {user?.role === 'SERVICE_ENGINEER' && (
                      <div>
                        <label className="block text-sm font-bold text-slate-800 mb-3 uppercase tracking-wide">
                          Add Remarks
                          {formData.status === 'RETURN' && (
                            <span className="ml-2 text-red-600 font-bold">*</span>
                          )}
                        </label>
                        {formData.status === 'RETURN' && (
                          <div className="mb-3 p-3 bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-300 rounded-xl">
                            <p className="text-sm font-semibold text-red-700">⚠️ Remarks are mandatory for RETURN status. Please provide a reason for the return.</p>
                          </div>
                        )}
                        <textarea
                          rows={4}
                          placeholder={formData.status === 'RETURN' ? "Enter reason for return (required)..." : "Add any notes or observations here..."}
                          value={formData.remarks}
                          onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                          className={`w-full px-5 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:border-purple-500 text-sm font-medium transition bg-white shadow-sm resize-none ${
                            formData.status === 'RETURN' && !formData.remarks.trim()
                              ? 'border-red-400 focus:ring-red-500'
                              : 'border-slate-300 focus:ring-purple-500'
                          }`}
                        />
                      </div>
                    )}

                    {/* Submit Button - Large and prominent */}
                    <button
                      type="submit"
                      disabled={updating}
                      className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 rounded-xl hover:from-green-600 hover:to-emerald-700 transition disabled:from-slate-400 disabled:to-slate-500 font-bold text-lg shadow-lg transform hover:scale-105"
                    >
                      {updating ? (
                        <>
                          <Loader className="w-6 h-6 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        <>Update Record</>
                      )}
                    </button>
                  </form>
                </div>
              )}

              {/* Information Card - Shows on desktop when no barcode scanned */}
              {!scannedData && (
                <div className="hidden lg:block lg:col-span-2 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-2xl shadow-lg p-8 border border-indigo-200">
                  <div className="h-full flex flex-col justify-between">
                    <div>
                      <h3 className="text-3xl font-bold text-slate-900 mb-6">How It Works</h3>
                      
                      <div className="space-y-6">
                        {/* Step 1 */}
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                            <span className="text-white font-bold text-xl">1</span>
                          </div>
                          <div>
                            <h4 className="text-lg font-bold text-slate-900 mb-2">Scan the Barcode</h4>
                            <p className="text-slate-700 font-medium">Use the camera or manually enter the barcode number in the field on the left.</p>
                          </div>
                        </div>

                        {/* Step 2 */}
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                            <span className="text-white font-bold text-xl">2</span>
                          </div>
                          <div>
                            <h4 className="text-lg font-bold text-slate-900 mb-2">Select Status</h4>
                            <p className="text-slate-700 font-medium">Choose the appropriate status: Pending, Assisted, Hold, or Return.</p>
                          </div>
                        </div>

                        {/* Step 3 */}
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                            <span className="text-white font-bold text-xl">3</span>
                          </div>
                          <div>
                            <h4 className="text-lg font-bold text-slate-900 mb-2">Update Record</h4>
                            <p className="text-slate-700 font-medium">Add remarks if needed, then click the Update button to save changes.</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quick Tips */}
                    <div className="mt-8 pt-8 border-t-2 border-indigo-300">
                      <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-4">Quick Tips</h4>
                      <ul className="space-y-3 text-sm text-slate-700 font-medium">
                        <li className="flex gap-2">
                          <span className="text-indigo-600">•</span>
                          <span>Use the camera for quick scanning in the field</span>
                        </li>
                        <li className="flex gap-2">
                          <span className="text-indigo-600">•</span>
                          <span>Manual entry is available for offline situations</span>
                        </li>
                        <li className="flex gap-2">
                          <span className="text-indigo-600">•</span>
                          <span>Remarks are optional but recommended</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Floating Camera at Bottom on Mobile */}
      {useCamera && (
        <div className="fixed inset-0 lg:hidden z-50 flex flex-col">
          {/* Full screen overlay */}
          <div className="flex-1 bg-black relative">
            <div ref={mobileQuaggaRef} style={{ width: '100%', height: '100%' }} />
            
            {/* Status indicator */}
            {cameraActive && (
              <div className="absolute top-6 left-6 flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                <div className="w-2.5 h-2.5 bg-white rounded-full animate-pulse"></div>
                Ready to Scan
              </div>
            )}
          </div>

          {/* Bottom control button */}
          <button
            type="button"
            onClick={() => stopCamera()}
            className="bg-slate-900 border-t border-slate-700 px-4 py-4 text-white flex items-center justify-center gap-3 font-bold text-lg hover:bg-black transition"
          >
            <X className="w-6 h-6" />
            Close Camera
          </button>
        </div>
      )}
    </div>
  );
};

export default ScanUpdate;
